import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { LoadingOverlay, Group } from "@mantine/core"
import {
  DepartmentOrWorkAreaId,
  isDepartmentId,
  isWorkAreaId,
  parseDepartmentId,
  parseWorkAreaId,
} from "../../../../-components/tree/department-or-workarea-id"
import { DepartmentDutyRosterSpreadsheet } from "../../-components/DutyScheduling/duty-roster-spreadsheet"
import { SuspenseWithErrorBoundary } from "../../../../-components/suspense"
import { useValidatedState } from "@mantine/hooks"
import { Page } from "../../../../-components/Page"
import { DepartmentAndWorkareaTreeSelect } from "../-components/DepartmentAndWorkareaTreeSelect"
import { keepPreviousData } from "@tanstack/react-query"
import { DatePeriodSelect } from "../orchestra/-components/period-select"
import { useGridStoreInitializer } from "../../-components/DutyScheduling/state"
import { DutySchedulingContainer } from "../-components/-DutySchedulingContainer"
import { useDepartmentDutyRosterGet, useGetFormulaResults } from "@/orval/gen"
import { dateToDateOnly } from "../../../../utils/date-to-date-only"
import { collectContractIds } from "../-components/collect-contracts"
import { DateOnly } from "../../../../@client/System"

export const Route = createFileRoute("/app/duties/shifts-and-casts/")({
  component: RouteComponent,
  beforeLoad: ({ context: { t } }) => ({
    getTitle: () => t("DutyScheduling_ShiftsAndCasts"),
  }),
})

const d = new Date()
d.setDate(new Date().getDate() + 14)
const initialRange: [DateOnly | null, DateOnly | null] = [
  dateToDateOnly(new Date()),
  dateToDateOnly(d),
]

function RouteComponent() {
  const [{ value: range, lastValidValue: lastValidRange }, setRange] =
    useValidatedState(initialRange, (v) => Boolean(v[0] && v[1]))

  const [selectedWorkareasOrDepartments, setSelectedWorkareasOrDepartments] =
    useState<DepartmentOrWorkAreaId[]>([])

  const departmentIds = selectedWorkareasOrDepartments
    .filter(isDepartmentId)
    .map(parseDepartmentId)
  const workareaIds = selectedWorkareasOrDepartments
    .filter(isWorkAreaId)
    .map(parseWorkAreaId)

  return (
    <Page>
      <Group>
        <DepartmentAndWorkareaTreeSelect
          focus="Casts"
          mode="single"
          onlyDispo={false}
          onSelectionChanged={(v) => {
            if (v === null || v.length === 0) {
              return
            }
            setSelectedWorkareasOrDepartments(v)
          }}
        />
        <DatePeriodSelect value={range} onChange={(v) => setRange(v)} />
      </Group>
      <DutySchedulingContainer>
        {lastValidRange && lastValidRange[0] && lastValidRange[1] ? (
          <InnerComponent
            from={lastValidRange[0]}
            until={lastValidRange[1]}
            departmentIds={
              departmentIds?.length === 0 ? undefined : departmentIds
            }
            workareaIds={workareaIds?.length === 0 ? undefined : workareaIds}
          />
        ) : null}
      </DutySchedulingContainer>
    </Page>
  )
}

function InnerComponent({
  from,
  until,
  departmentIds,
  workareaIds,
}: {
  from: string
  until: string
  departmentIds: number[] | undefined
  workareaIds: number[] | undefined
}) {
  const { data, refetch, isFetching, dataUpdatedAt } =
    useDepartmentDutyRosterGet(
      {
        from,
        until,
        departmentIds,
        workareaIds,
        focus: "Shifts",
      },
      { query: { placeholderData: keepPreviousData } }
    )
  const contractIds = collectContractIds(data?.rowHeaders || [])
  const departmentId = departmentIds?.[0] || undefined

  const { data: formulaResults } = useGetFormulaResults(
    {
      from,
      until,
      contractIds,
      departmentId,
      formulaIds: [],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        enabled: Boolean(departmentId),
      },
    }
  )
  const formulaValues = formulaResults?.values

  const { storeInitialized, updateFormulaResults } = useGridStoreInitializer({
    data: data,
    compact: false,
    updatedAt: dataUpdatedAt,
  })

  useEffect(() => {
    if (formulaValues) {
      updateFormulaResults(formulaValues)
    }
  }, [formulaValues])

  return (
    <div style={{ position: "relative", minHeight: 200, minWidth: 200 }}>
      {data && storeInitialized ? (
        <SuspenseWithErrorBoundary>
          <DepartmentDutyRosterSpreadsheet
            departmentId={departmentId}
            focus="cast-planning"
            onRefetch={() => {
              refetch()
            }}
            data={data}
          />
        </SuspenseWithErrorBoundary>
      ) : null}
      <LoadingOverlay
        visible={isFetching}
        zIndex={555}
        overlayProps={{ radius: "sm", blur: 0 }}
      />
    </div>
  )
}
